import {useEffect, useState} from 'react';

const DISMISS_KEY = 'pwa-install-dismissed-until';

function isStandalone() {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function detectIos() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isIPhoneOrPod = /iPad|iPhone|iPod/.test(ua);
    const isIpadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return isIPhoneOrPod || isIpadOS;
}

function isDismissed() {
    try {
        const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
        return until > Date.now();
    } catch {
        return false;
    }
}

export function useInstallPrompt() {
    const [deferredEvent, setDeferredEvent] = useState(null);
    const [canShow, setCanShow] = useState(false);
    const [isIos] = useState(detectIos());

    useEffect(() => {
        if (isStandalone() || isDismissed()) return;

        if (isIos) {
            setCanShow(true);
            return;
        }

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredEvent(e);
            setCanShow(true);
        };
        const onInstalled = () => {
            setCanShow(false);
            setDeferredEvent(null);
        };
        window.addEventListener('beforeinstallprompt', onBeforeInstall);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstall);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, [isIos]);

    const promptInstall = async () => {
        if (!deferredEvent) return null;
        deferredEvent.prompt();
        const choice = await deferredEvent.userChoice;
        setDeferredEvent(null);
        setCanShow(false);
        return choice.outcome;
    };

    const dismiss = (days = 30) => {
        try {
            const until = Date.now() + days * 24 * 60 * 60 * 1000;
            localStorage.setItem(DISMISS_KEY, String(until));
        } catch {
            // ignore
        }
        setCanShow(false);
    };

    return {canShow, isIos, promptInstall, dismiss};
}
